import { useEffect, useMemo, useRef, useState } from 'react';
import { destinations } from '../data';
import { CalendarIcon } from './Icons';
import {
  DAYS,
  MONTHS,
  addDays,
  formatLongDate,
  formatShortDate,
  isBefore,
  monthMatrix,
  occupancyLabel,
  sameDay,
  startOfDay,
} from '../utils';

function inStayRange(day, checkIn, checkOut) {
  if (!day || !checkIn || !checkOut) return false;
  return (sameDay(day, checkIn) || isBefore(checkIn, day)) && (sameDay(day, checkOut) || isBefore(day, checkOut));
}

function DateRange({ search, setSearch, open, setOpen }) {
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const weeks = monthMatrix(cursor.getFullYear(), cursor.getMonth());

  const pick = (day) => {
    if (!day || isBefore(day, today)) return;
    const startingNew = !search.checkIn || search.checkOut || isBefore(day, search.checkIn);
    if (startingNew) {
      setSearch((s) => ({ ...s, checkIn: day, checkOut: null }));
      return;
    }
    setSearch((s) => ({ ...s, checkOut: sameDay(day, s.checkIn) ? addDays(day, 1) : day }));
    setOpen(false);
  };

  const label = (date, fallback) => (date ? formatShortDate(date) : fallback);

  return (
    <div className="search-dates">
      <button
        type="button"
        className="search-date-btn"
        aria-label="Check-in"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <CalendarIcon />
        <span>
          <em>Check-in</em>
          {label(search.checkIn, 'Add date')}
        </span>
      </button>
      <button
        type="button"
        className="search-date-btn"
        aria-label="Check-out"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <CalendarIcon />
        <span>
          <em>Check-out</em>
          {label(search.checkOut, 'Add date')}
        </span>
      </button>
      {open && (
        <div className="calendar-pop" role="dialog" aria-label="Choose stay dates">
          <div className="calendar-nav">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            >
              ‹
            </button>
            <strong>{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</strong>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            >
              ›
            </button>
          </div>
          <div className="calendar-weekdays">
            {DAYS.map((d) => <span key={d}>{d}</span>)}
          </div>
          {weeks.map((week, i) => (
            <div className="calendar-week" key={i}>
              {week.map((day, j) => {
                if (!day) return <span key={j} />;
                const disabled = isBefore(day, today);
                const start = sameDay(day, search.checkIn);
                const end = sameDay(day, search.checkOut);
                const range = inStayRange(day, search.checkIn, search.checkOut);
                return (
                  <button
                    key={j}
                    type="button"
                    disabled={disabled}
                    className={`cal-day${start ? ' start' : ''}${end ? ' end' : ''}${range ? ' in-range' : ''}`}
                    aria-label={formatLongDate(day)}
                    onClick={() => pick(day)}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GuestPicker({ search, setSearch, open, setOpen }) {
  const adults = search.occupancy.adults;
  const setAdults = (n) => setSearch((s) => ({ ...s, occupancy: { adults: n } }));

  return (
    <div className="search-guests">
      <button
        type="button"
        className="search-date-btn"
        aria-label="Guests"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>
          <em>Guests</em>
          {occupancyLabel(search.occupancy)}
        </span>
      </button>
      {open && (
        <div className="guest-pop" role="dialog" aria-label="Choose guests">
          <p>Adults</p>
          <div className="guest-stepper">
            <button type="button" aria-label="Fewer adults" disabled={adults <= 1} onClick={() => setAdults(adults - 1)}>−</button>
            <span>{adults}</span>
            <button type="button" aria-label="More adults" disabled={adults >= 4} onClick={() => setAdults(adults + 1)}>+</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SearchForm({ search, setSearch, onSearch, className = '' }) {
  const [openSuggest, setOpenSuggest] = useState(false);
  const [openDates, setOpenDates] = useState(false);
  const [openGuests, setOpenGuests] = useState(false);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = (search.destination || '').trim().toLowerCase();
    if (!q) return destinations;
    return destinations.filter((d) => `${d.name} ${d.country}`.toLowerCase().includes(q));
  }, [search.destination]);

  useEffect(() => {
    const close = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setOpenSuggest(false);
        setOpenDates(false);
        setOpenGuests(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setOpenSuggest(false);
    setOpenDates(false);
    setOpenGuests(false);
    onSearch();
  };

  const pickPlace = (name) => {
    setSearch((s) => ({ ...s, destination: name }));
    setOpenSuggest(false);
  };

  return (
    <form className={`home-search ${className}`.trim()} onSubmit={submit} ref={wrapRef}>
      <div className="search-where">
        <input
          aria-label="Search apartments"
          placeholder="Dar es Salaam"
          value={search.destination}
          onChange={(e) => setSearch({ ...search, destination: e.target.value })}
          onFocus={() => {
            setOpenSuggest(true);
            setOpenDates(false);
            setOpenGuests(false);
          }}
        />
        {openSuggest && (
          <div className="search-suggest">
            {matches.map((d) => (
              <button key={d.id} type="button" onClick={() => pickPlace(d.name)}>
                <strong>{d.name}</strong>
                <span>{d.country}</span>
              </button>
            ))}
            {matches.length === 0 && <p>No matching areas</p>}
          </div>
        )}
      </div>
      <DateRange
        search={search}
        setSearch={setSearch}
        open={openDates}
        setOpen={(next) => {
          setOpenDates(next);
          if (next) {
            setOpenSuggest(false);
            setOpenGuests(false);
          }
        }}
      />
      <GuestPicker
        search={search}
        setSearch={setSearch}
        open={openGuests}
        setOpen={(next) => {
          setOpenGuests(next);
          if (next) {
            setOpenSuggest(false);
            setOpenDates(false);
          }
        }}
      />
      <button type="submit">Search</button>
    </form>
  );
}
